import AppBar from '@mui/material/AppBar';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { dialogManager } from '../stores/DialogManager';

import { CloseYoutubeButton } from './CloseYoutubeButton';
import { ShareYoutubeLinkButton } from './ShareYoutubeLinkButton';
import { YoutubeLoadingDialog } from './YoutubeLoadingDialog';

import { isReactNativeApp } from '@egr/xbox/app-api/AppDetection';
import { appInfo } from '@egr/xbox/app-api/AppInfo';
import { cookieDialogStore } from '@egr/xbox/egr-gui-dialogs/cookieDialog/CookieDialogStore';
import Dialog from '@egr/xbox/egr-gui-dialogs/utils/DialogOverride';
import { TopToolbar } from '@egr/xbox/egr-gui-elements/Toolbar/TopToolbar';
import { DialogTransition } from '@egr/xbox/egr-gui-elements/single-components/DialogTransition';
import { FlexSpacer } from '@egr/xbox/egr-gui-elements/single-components/FlexSpacer';
import { corsProxyUrl } from '@egr/xbox/utils/Services';
import { makeStyles } from '@egr/xbox/utils/tss-react';

const useYoutubeDialogStyles = makeStyles({ name: 'YoutubeDialog' })((theme) => ({
    content: {
        overflow: 'hidden',
        position: 'relative',
        width: '65%',
        height: '65%',
        border: 'none',
        margin: 'auto',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            flex: '0 0 100%',
        },
    },
    iframe: {
        border: 'none',
        width: '100%',
        height: '100%',
    },
    container: {
        overflow: 'hidden',
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    noPadding: {
        padding: 0,
    },
    appBar: {
        backgroundColor: '#3C3C3C !important',
        color: '#FFF !important',
    },
    blur: {
        width: '100%',
        height: '100%',
        backgroundColor: 'black',
        opacity: 0.7,
        position: 'absolute',
    },
    center: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
    },
    text: {
        width: '65%',
        display: 'flex',
        textAlign: 'center',
        flexDirection: 'column',
        alignItems: 'center',
    },
    image: {
        height: 'auto',
        width: 'auto',
        minHeight: '100%',
        minWidth: '100%',
    },
}));

export const YoutubeDialog: React.FC = () => {
    const { classes, cx } = useYoutubeDialogStyles();
    const { t } = useTranslation();
    const { youtubeLinkId, youtubeDialogOpen, youtubeWarning } = dialogManager.useState(['youtubeLinkId', 'youtubeDialogOpen', 'youtubeWarning']);
    const youtubeCookieEnabled = cookieDialogStore.useState(['youtube']).youtube ?? false;
    const [allowPlayer, setAllowPlayer] = React.useState(false);
    React.useEffect(
        () => {
            if (!youtubeCookieEnabled) {
                dialogManager.updateState({ youtubeWarning: true });
            }
            setAllowPlayer(youtubeCookieEnabled);
        },
        [youtubeCookieEnabled]
    );

    if (!youtubeDialogOpen || !youtubeLinkId) {
        return null;
    }

    const nativeIOS = isReactNativeApp && appInfo?.app_platform === 'ios';
    const uri = youtubeLinkId != null ? `https://www.youtube-nocookie.com/embed/${youtubeLinkId}?rel=0&modestbranding=1&autoplay=1` : '';

    function onButtonClick() {
        if (nativeIOS) {
            dialogManager.updateState({
                youtubeDialogOpen: false,
                youtubeWarning: false,
            });
        }
        setAllowPlayer(true);
    }

    return (
        <React.Fragment>
            <YoutubeLoadingDialog />
            <Dialog
                fullWidth
                maxWidth="md"
                fullScreen
                open={youtubeDialogOpen}
                onClose={dialogManager.closeYoutube}
                TransitionComponent={DialogTransition}
                PaperProps={{ style: { background: 'transparent' } }}
            >
                <AppBar
                    position="absolute"
                    className={classes.appBar}
                >
                    <TopToolbar>
                        <CloseYoutubeButton />
                        <FlexSpacer />
                        <ShareYoutubeLinkButton />
                    </TopToolbar>
                </AppBar>
                <DialogContent
                    className={classes.noPadding}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <Backdrop
                        open
                        onClick={dialogManager.closeYoutube}
                    />
                    <div className={classes.container}>
                        <div className={classes.content}>
                            {allowPlayer && (
                                <iframe
                                    className={cx(classes.iframe, classes.center)}
                                    src={uri}
                                    allow="encrypted-media; gyroscope; picture-in-picture; allowfullscreen; autoplay;"
                                    allowFullScreen={true}
                                    onLoad={() => dialogManager.updateState({ youtubeWarning: false })}
                                />
                            )}
                            {youtubeWarning && (
                                <React.Fragment>
                                    <img
                                        className={cx(classes.image, classes.center)}
                                        src={corsProxyUrl(`http://img.youtube.com/vi/${youtubeLinkId ?? ''}/hqdefault.jpg`)}
                                    />
                                    <div className={classes.blur} />
                                    <div className={cx(classes.center, classes.text)}>
                                        <Typography
                                            variant="h6"
                                            color="textSecondary"
                                        >
                                            {t('YouTube privacy warning')}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            color="textSecondary"
                                        >
                                            {t(
                                                'Please click the button to play the video. Note that by starting the video, your data (e.g IP address) will be transmitted to YouTube.'
                                            )}
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={onButtonClick}
                                            style={{ margin: 8 }}
                                        >
                                            {t('Play video')}
                                        </Button>
                                    </div>
                                </React.Fragment>
                            )}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </React.Fragment>
    );
};

YoutubeDialog.displayName = 'YoutubeDialog';